import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { keycloakService } from "services/keycloakService";

// =============================================================================
// Types
// =============================================================================

interface MorningstarTokenResponse {
  success: boolean;
  data: {
    token: string;
    expiresIn: number; // seconds
    appId: string;
    user?: {
      sub: string;
      email: string;
      preferred_username: string;
    };
  };
}

// =============================================================================
// API Function
// =============================================================================

export const fetchMorningstarToken = async (): Promise<string> => {
  const baseUrl = process.env.REACT_APP_MORNINGSTAR_API_BASE_URL;

  if (!baseUrl) {
    throw new Error("REACT_APP_MORNINGSTAR_API_BASE_URL is not defined");
  }

  const keycloakToken = await keycloakService.getToken();

  if (!keycloakToken) {
    throw new Error("No Keycloak token available");
  }

  const response = await axios.post<MorningstarTokenResponse>(
    `${baseUrl}/api/auth/token-by-keycloak`,
    { keycloakToken },
    {
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  // 204 No Content means the server is warming up (cold start) — treat as retryable
  if (response.status === 204 || !response.data) {
    throw new Error("Morningstar API is warming up, retrying...");
  }

  if (!response.data.success || !response.data.data?.token) {
    throw new Error("Failed to get Morningstar token");
  }

  return response.data.data.token;
};

// =============================================================================
// Hook
// =============================================================================

const REFRESH_BUFFER_SECONDS = 60 * 5; // refresh 5 minutes before expiry
const DEFAULT_EXPIRES_IN_SECONDS = 86400; // 24 hours fallback

/**
 * Hook to get and manage Morningstar API authentication token.
 * Exchanges the user's FA Keycloak token for a Morningstar JWT via
 * POST /api/auth/token-by-keycloak. Token is cached and refreshed before expiry.
 */
export const useMorningstarToken = () => {
  const expiresIn = DEFAULT_EXPIRES_IN_SECONDS - REFRESH_BUFFER_SECONDS;
  const staleMs = expiresIn * 1000;

  const query = useQuery({
    queryKey: ["morningstar", "token"],
    queryFn: fetchMorningstarToken,
    staleTime: staleMs,
    cacheTime: staleMs + 60 * 1000,
    retry: 6,
    // Exponential backoff: 2s, 4s, 8s, 16s, 30s, 30s — handles cold starts that can take 30+ seconds
    retryDelay: (attempt) => Math.min(2000 * Math.pow(2, attempt), 30000),
  });

  return {
    token: query.data,
    // isFetching stays true during all retry attempts, unlike isLoading which only covers the initial fetch
    isLoading: query.isFetching,
    error: query.isError && !query.isFetching ? query.error : null,
    refetch: query.refetch,
  };
};
